// Ported from dev-scrips-pm-react src/billing/services/use-service-search.ts (PROD-1210 Provider Fees).
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import type { BillingServiceListModel } from '@/shared/types/billing';

/**
 * Service typeahead — `GET /api/Billing/Services`.
 *
 * Param order and conditional inclusion mirror Flutter's
 * `billing_data_source_impl.dart:233-261` exactly:
 *
 *   showArchived={bool}&pageSize={N}&pageNumber={N}
 *   [&locationId={id}]   (only when non-blank)
 *   [&q={query}]         (only when non-blank)
 *   [&serviceListId={id}](only when non-blank — Flutter passes "" so it's skipped)
 *   [&providerId={id}]   (only when non-blank — the Provider Fees edit form
 *                          leaves this unset so the typeahead searches the
 *                          whole catalog, not the provider's existing fees)
 */

interface UseServiceSearchParams {
  query: string;
  /** When set, narrows results to a specific provider. Leave unset for the Provider Fees typeahead. */
  providerId?: string;
  /** Optional location scope. */
  locationId?: string;
  /** Optional service-list scope. */
  serviceListId?: string;
  pageSize?: number;
  /** Set to false to disable the query (e.g. while the popover is closed). */
  enabled?: boolean;
}

export function useServiceSearch(params: UseServiceSearchParams) {
  const {
    query,
    providerId,
    locationId,
    serviceListId,
    pageSize = 20,
    enabled = true,
  } = params;
  return useQuery({
    queryKey: [
      'service-search',
      providerId ?? null,
      locationId ?? null,
      serviceListId ?? null,
      query,
    ] as const,
    queryFn: ({ signal }) => {
      // Build manually (not URLSearchParams) to preserve Flutter's exact
      // parameter order and to skip blanks the same way `isBlank()` does
      // in the Dart source — empty string counts as blank.
      const parts: string[] = [
        `showArchived=false`,
        `pageSize=${pageSize}`,
        `pageNumber=1`,
      ];
      if (locationId) parts.push(`locationId=${encodeURIComponent(locationId)}`);
      if (query) parts.push(`q=${encodeURIComponent(query)}`);
      if (serviceListId) parts.push(`serviceListId=${encodeURIComponent(serviceListId)}`);
      if (providerId) parts.push(`providerId=${encodeURIComponent(providerId)}`);

      return get<BillingServiceListModel>(
        `${BILLING_BASE}/Services?${parts.join('&')}`,
        { signal },
      );
    },
    enabled: enabled && query.trim().length >= 1,
    staleTime: 1000 * 30,
  });
}
